import {useState} from 'react';
import Modal from 'components/Modal';
import Typography from 'components/Typography';
import TextField from 'components/TextField';
import SelectField from 'components/SelectField';
import Button from 'components/Button';
import {useForm, Controller} from 'react-hook-form';
import {SelectOption, Webhook} from 'types';
import {format} from 'date-fns';
import toast from 'react-hot-toast';
import cn from 'classnames';
import ReactSwitch from 'react-switch';
import CopyIcon from '../../../icons/copy';
import DeliveryHistory from './DeliveryHistory';
import styles from '../WebhookSettings.module.scss';

type DetailTab = 'configuration' | 'delivery_history';

type WebhookDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  webhook: Webhook;
  onDelete: () => void;
  onUpdate: (data: {
    id: string;
    name: string;
    url: string;
    description: string;
    events: string[];
    is_active: boolean;
  }) => Promise<void>;
  isUpdating: boolean;
  onRegenerateSecret: () => void;
  isRegenerating: boolean;
  generatedSecret: string | null;
  eventOptions: SelectOption[];
};

type ConfigFormData = {
  name: string;
  url: string;
  description: string;
  events: SelectOption[];
  is_active: boolean;
};

function WebhookDetailModal({
  isOpen,
  onClose,
  webhook,
  onDelete,
  onUpdate,
  isUpdating,
  onRegenerateSecret,
  isRegenerating,
  generatedSecret,
  eventOptions,
}: WebhookDetailModalProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('configuration');

  const defaultEventValues = eventOptions.filter((opt) =>
    webhook.events.includes(opt.value as string)
  );

  const {
    register,
    control,
    formState: {errors},
    handleSubmit,
  } = useForm<ConfigFormData>({
    defaultValues: {
      name: webhook.name,
      url: webhook.url,
      description: webhook.description,
      events: defaultEventValues,
      is_active: webhook.is_active,
    },
  });

  const handleSave = (data: ConfigFormData) => {
    onUpdate({
      id: webhook.id,
      name: data.name,
      url: data.url,
      description: data.description,
      events: data.events.map((e) => e.value as string),
      is_active: data.is_active,
    });
  };

  const handleCopySecret = () => {
    if (!generatedSecret) return;
    navigator.clipboard.writeText(generatedSecret);
    toast.success('Secret copied to clipboard');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      className={styles.detailModal}
    >
      <Typography variant="h2" className={styles.detailTitle}>
        {webhook.name}
      </Typography>

      <div className={styles.modalTabs}>
        <button
          type="button"
          className={cn(styles.modalTab, {
            [styles.modalTabActive]: activeTab === 'configuration',
          })}
          onClick={() => setActiveTab('configuration')}
        >
          Configuration
        </button>
        <button
          type="button"
          className={cn(styles.modalTab, {
            [styles.modalTabActive]: activeTab === 'delivery_history',
          })}
          onClick={() => setActiveTab('delivery_history')}
        >
          Delivery History
        </button>
      </div>

      {activeTab === 'configuration' && (
        <div className={styles.configContent}>
          <TextField
            label="Name"
            placeholder="Enter webhook name"
            {...register('name', {required: 'Name is required'})}
            error={errors.name?.message}
          />
          <TextField
            label="Endpoint URL"
            placeholder="Enter endpoint URL"
            {...register('url', {required: 'URL is required'})}
            error={errors.url?.message}
          />
          <TextField
            label="Description"
            placeholder="Enter description"
            {...register('description')}
          />
          <Controller
            name="events"
            control={control}
            rules={{required: 'At least one event is required'}}
            render={({field: {ref, ...field}, fieldState}) => (
              <SelectField
                label="Events"
                placeholder="Select events"
                options={eventOptions}
                // @ts-ignore
                isMulti
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <div className={styles.detailRow}>
            <Typography variant="label" className={styles.detailLabel}>
              Status
            </Typography>
            <Controller
              name="is_active"
              control={control}
              render={({field}) => (
                <div>
                  <ReactSwitch
                    onColor="#4285f4"
                    onChange={field.onChange}
                    handleDiameter={17}
                    height={20}
                    width={40}
                    checked={field.value}
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </div>
              )}
            />
          </div>

          <div className={styles.detailRow}>
            <Typography variant="label" className={styles.detailLabel}>
              Created
            </Typography>
            <Typography variant="p" className={styles.detailValue}>
              {format(new Date(webhook.created_at), 'MMM d, yyyy hh:mm aa')}
            </Typography>
          </div>

          <div className={styles.detailRow}>
            <Typography variant="label" className={styles.detailLabel}>
              Updated
            </Typography>
            <Typography variant="p" className={styles.detailValue}>
              {format(new Date(webhook.updated_at), 'MMM d, yyyy hh:mm aa')}
            </Typography>
          </div>

          {generatedSecret && (
            <div className={styles.secretRow}>
              <Typography variant="label" className={styles.detailLabel}>
                Secret
              </Typography>
              <div className={styles.secretValue}>
                <code className={styles.secretCode}>{generatedSecret}</code>
                <CopyIcon
                  width={16}
                  height={16}
                  className={styles.copyIcon}
                  onClick={handleCopySecret}
                />
              </div>
              <Typography variant="p" className={styles.secretHint}>
                Copy this secret now. You won't be able to see it again.
              </Typography>
            </div>
          )}

          <div className={styles.detailActions}>
            <div className={styles.detailActionsLeft}>
              <Button
                color="outlined"
                onClick={onRegenerateSecret}
                loading={isRegenerating}
              >
                Regenerate Secret
              </Button>
              <Button color="danger" onClick={onDelete}>
                Delete
              </Button>
            </div>
            <Button
              color="primary"
              onClick={handleSubmit(handleSave)}
              loading={isUpdating}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'delivery_history' && (
        <div className={styles.deliveryTabContent}>
          <DeliveryHistory webhookId={webhook.id} />
        </div>
      )}
    </Modal>
  );
}

export default WebhookDetailModal;
